import API_CONFIG from './config';

/**
 * API Client for making HTTP requests
 */
class ApiClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Set authorization token for authenticated requests
   * @param {string} token - JWT token
   */
  setAuthToken(token) {
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} queryParams - Query parameters
   * @returns {Promise} - Response data
   */
  async get(endpoint, queryParams = {}) {
    const url = this._buildUrl(endpoint, queryParams);
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      });
      
      return this._handleResponse(response);
    } catch (error) {
      return this._handleError(error);
    }
  }

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise} - Response data
   */
  async post(endpoint, data = {}) {
    const url = this._buildUrl(endpoint);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(data),
      });
      
      return this._handleResponse(response);
    } catch (error) {
      return this._handleError(error);
    }
  }

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise} - Response data
   */
  async put(endpoint, data = {}) {
    const url = this._buildUrl(endpoint);
    
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(data),
      });
      
      return this._handleResponse(response);
    } catch (error) {
      return this._handleError(error);
    }
  }

  /**
   * Make a PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise} - Response data
   */
  async patch(endpoint, data = {}) {
    const url = this._buildUrl(endpoint);
    
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: this.headers,
        body: JSON.stringify(data),
      });
      
      return this._handleResponse(response);
    } catch (error) {
      return this._handleError(error);
    }
  }

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise} - Response data
   */
  async delete(endpoint) {
    const url = this._buildUrl(endpoint);
    
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.headers,
      });
      
      return this._handleResponse(response);
    } catch (error) {
      return this._handleError(error);
    }
  }

  /**
   * Handle API response
   * @param {Response} response - Fetch Response object
   * @returns {Promise} - Parsed response data
   * @private
   */
  async _handleResponse(response) {
    const data = await response.json();
    
    if (!response.ok) {
      const error = {
        status: response.status,
        statusText: response.statusText,
        data,
      };
      
      return Promise.reject(error);
    }
    
    return data;
  }

  /**
   * Handle request error
   * @param {Error} error - Error object
   * @returns {Promise} - Rejected promise with error
   * @private
   */
  _handleError(error) {
    console.error('API request failed:', error);
    
    return Promise.reject({
      status: 0,
      statusText: 'Network Error',
      data: { message: 'Network request failed' },
      originalError: error,
    });
  }

  /**
   * Build URL with query parameters
   * @param {string} endpoint - API endpoint
   * @param {Object} queryParams - Query parameters
   * @returns {string} - Complete URL
   * @private
   */
  _buildUrl(endpoint, queryParams = {}) {
    const url = new URL(this.baseURL + endpoint);
    
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null) {
        url.searchParams.append(key, queryParams[key]);
      }
    });
    
    return url.toString();
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient; 